import { useCallback, useEffect, useState } from "react";
import { useTranslate } from "@/hooks/use-translate/useTranslate";
import closeSymbol from "@/assets/images/commons/RedCloseX.svg";
import { useAxios } from "@/hooks/use-axios/useAxios";
import SnStandQuery from "./components/SnStandQuery";
import routes from "@/assets/routes/routes";
import LinkButton from "@/components/buttons/link-button/LinkButton";
import NewInspectionForm from "./components/NewInspectionForm";
import { type NewInspection } from "./InspectionCreator.d";
import { Icon } from "@/components/icons/Icon";
import ProblemMessage from "@/components/problem-message/ProblemMessage";
import FormattedTranslation from "@/components/formatted-translation/FormattedTranslation";
import { Link } from "react-router-dom";
import { BASE_URL, STATUS } from "@/assets/constants/constants";

interface InspectionCreatorProps {
  closeCreator: () => void;
}

const InspectionCreator = ({ closeCreator }: InspectionCreatorProps) => {
  const translate = useTranslate();
  const { request } = useAxios();

  const [snStand, setSnStand] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [inspectionExist, setInpectionExist] = useState(false);
  const [newInspectionData, setNewInspectionData] =
    useState<NewInspection | null>(null);
  const [standInspectionId, setStandInspectionId] = useState<string>("");

  useEffect(() => {
    return () => {
      setSnStand("");
      setError("");
      setNewInspectionData(null);
      setInpectionExist(false);
    };
  }, []);

  const changeSnStand = useCallback((snData: string) => setSnStand(snData), []);

  const getInspection = useCallback(async () => {
    try {
      if (error) setError("");
      if (inspectionExist) setInpectionExist(false);
      const result = await request({
        url: `${routes.getInspectionBySnStand}${snStand}`,
      });

      if (result) {
        if (error) setError("");
        if (result.status === STATUS.IMPORTED) {
          const newResult = {
            nsStand: result.nsStand,
            startDate: new Date(),
            engineId: result.engineId || undefined,
            engineName: result.engineName || undefined,
            project: result.project || undefined,
            esnIn: result.esnIn || undefined,
            nsCradle: result.nsCradle || undefined,
            owner: result.owner || undefined,
            colour: result.colour || undefined,
            isCsPmo: result.isCsPmo || undefined,
            id: result.id,
          };
          setNewInspectionData(newResult);
        } else if (result.id != null) {
          setInpectionExist(true);
          setStandInspectionId(result.id.toString());
        } else {
          setError("noPossibleInformation");
        }
      } else {
        throw new Error("NOT_FOUND");
      }
    } catch (err: any) {
      setInpectionExist(false);
      setStandInspectionId("");

      if (err instanceof Error && err.message === "NOT_FOUND") {
        setError("canNotCreateInspection");
      } else if (err.status === 404) setError("canNotCreateInspection");
      else setError("noPossibleInformation");
    }
  }, [error, inspectionExist, request, snStand]);

  const handleErrorMessage = useCallback(
    (errorMsg: string) => setError(errorMsg),
    [],
  );

  const renderError = useCallback(() => {
    if (!error.length) return null;
    if (error === "canNotCreateInspection")
      return (
        <ProblemMessage
          titleLabel={"aProblem"}
          error={error}
          textData={[snStand]}
          closeMessage={() => setError("")}
        />
      );
    return (
      <ProblemMessage
        titleLabel={"error"}
        error={error}
        closeMessage={() => setError("")}
      />
    );
  }, [error, snStand]);

  return (
    <div
      data-testid="inspector-creator"
      className="fixed overflow-auto bg-ib-black bg-opacity-70 w-[100vw] min-w-[100vw] h-[100vh] min-h-[100vh] top-0 right-0 z-[1000]"
    >
      {newInspectionData && !error ? (
        <div className="absolute top-5 w-[677px] max-w-[100vw] right-1/2 transform translate-x-1/2 border border-ib-blue-info-dark shadow-message-1 py-4 px-6 bg-ib-blue-info-light text-ib-main flex flex-row items-center gap-x-2">
          <Icon
            name="infoCircle"
            className="text-ib-blue-info-darkest size-4"
          />
          <div className="text-ib-black-light">
            {translate("noInspectionsForThisCar")}
          </div>
        </div>
      ) : null}
      {renderError()}
      {inspectionExist && !error ? (
        <div className="absolute top-5 w-[677px] max-w-[100vw] right-1/2 transform translate-x-1/2 border border-ib-yellow-warning-dark shadow-message-1 py-4 px-6 bg-ib-yellow-warning-light text-ib-main flex flex-row items-center justify-between">
          <div className="flex flex-row gap-x-2 items-center ">
            <Icon
              name="warningTriangle"
              className="text-ib-yellow-warning-darkest size-4"
            />
            <div className="text-ib-black-light">
              <FormattedTranslation
                label="inspectionsForThisCar"
                dataToInsert={[snStand]}
              />
            </div>
          </div>
          <div className="flex flex-row gap-x-1 items-center ">
            <Link to={`${BASE_URL}/${standInspectionId}`}>
              <LinkButton label={translate("goToInspection")} size="small" />
            </Link>
            <button
              className="w-4 h-4"
              onClick={() => setInpectionExist(false)}
            >
              <Icon
                name="close"
                className="text-ib-yellow-warning-darkest size-3"
                height="9.33px"
              />
            </button>
          </div>
        </div>
      ) : null}
      <div
        className="
        absolute top-[100px] right-1/2 transform translate-x-1/2 bg-ib-white w-fit p-6 rounded-[2px] shadow-modal-1
        max-md:fixed max-[767px]:bottom-0 max-md:top-auto
        max-md:left-0 max-md:right-0 max-md:translate-x-0
        max-md:w-full max-md:rounded-t-xl
        max-md:flex max-md:flex-col max-md:items-center
        "
      >
        <div className="w-full flex flex-row mb-4 justify-between items-center max-md:w-[520px] max-md:max-w-[calc(100vw-48px)]">
          <div className="font-ib-bold text-[24px] leading-[26px] font-[IberiaHeadlineBold]">
            {translate("newInspection")}
          </div>
          <button
            className="h-6 w-6 p-[5px] max-md:absolute max-md:right-6"
            onClick={closeCreator}
          >
            <img src={closeSymbol} />
          </button>
        </div>
        {!newInspectionData ? (
          <SnStandQuery
            closeCreator={closeCreator}
            searchInspection={getInspection}
            changeSnStand={changeSnStand}
            snStand={snStand}
          />
        ) : (
          <NewInspectionForm
            newInspectionData={newInspectionData}
            close={closeCreator}
            getError={handleErrorMessage}
          />
        )}
      </div>
    </div>
  );
};

export default InspectionCreator;
